import {
  isCoordinatesInsideBorough,
  parseZodErrors,
  partitionFileObjects,
  PartitionFileObjectsType,
  uploadPhotoSchema,
} from '@utils';
import { ZodError } from 'zod';
import {
  ErrorMessages,
  emptyServiceLocationDetails,
  ServiceLocationDetailsObjectType,
} from '@types';
import * as Sentry from '@sentry/react';
import React from 'react';
import { FileObject } from '@hooks';

interface Props {
  files: File[];
  setProcessingPhoto: React.Dispatch<React.SetStateAction<boolean>>;
  uploadedFiles: FileObject[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<FileObject[]>>;
  setErrorMessages: React.Dispatch<React.SetStateAction<ErrorMessages[]>>;
  updateUploadedImages: (uploadedImages: PartitionFileObjectsType) => void;
  updateIsPhotoLocationStored: (isPhotoLocationStored: boolean) => void;
  updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) => void;
  updateServiceLocationDetails: (
    serviceLocationDetails: ServiceLocationDetailsObjectType,
  ) => void;
  errorPath: string;
}

export const handleFileChange = async ({
  files,
  setProcessingPhoto,
  uploadedFiles,
  setUploadedFiles,
  setErrorMessages,
  updateUploadedImages,
  updateIsPhotoLocationStored,
  updateFileWithLoadedCoordinates,
  updateServiceLocationDetails,
  errorPath,
}: Props) => {
  if (!files || files.length === 0) {
    return;
  }
  setProcessingPhoto(true);

  const newFile = {
    file: files[files.length - 1],
    blob_endpoint: '',
    blob_uri: '',
  };

  const uploadedFileObjects =
    uploadedFiles.length > 0 ? [...uploadedFiles, newFile] : [newFile];
  setUploadedFiles(uploadedFileObjects);

  try {
    uploadPhotoSchema.parse({ uploadedFiles: uploadedFileObjects });

    setUploadedFiles(uploadedFileObjects);
    const partitionedFiles = await partitionFileObjects(uploadedFileObjects);
    updateUploadedImages(partitionedFiles);
    setErrorMessages([]);
    if (partitionedFiles.geoDataArrays.length > 0) {
      const { latitude, longitude } = partitionedFiles.geoDataArrays[0];

      const isInside = await isCoordinatesInsideBorough([
        latitude as number,
        longitude as number,
      ]);
      if (isInside) {
        updateIsPhotoLocationStored(true);
        updateFileWithLoadedCoordinates(
          partitionedFiles.geoDataArrays[0].fileName as string,
        );
        updateServiceLocationDetails({
          ...emptyServiceLocationDetails,
          latitude,
          longitude,
        });
      }

      if (latitude && longitude) {
        return [latitude, longitude];
      }
    }
  } catch (error) {
    if (error instanceof ZodError) {
      const validationErrors = parseZodErrors(error);
      setErrorMessages(
        validationErrors.map((error: ErrorMessages) => ({
          ...error,
          path: errorPath,
        })),
      );
    } else {
      Sentry.captureException(error);
      setErrorMessages([
        { path: errorPath, message: (error as Error).message },
      ]);
    }
  } finally {
    setProcessingPhoto(false);
  }
};

/**
 * Function that update the uploaded images filename, filetype, blob, geodata in sessionStorage
 * Refer to Parks and Trees for the implementation
 * @param uploadedFileParts object containing uploadedImages, uploadedImagesFileName, uploadedImagesFileType, uploadedImagesBlob, geoDataArrays
 * @param serviceLocationDetails service location details object
 * @param updateStoreByKey function to update the store by key name and value
 */

export const handleUpdateUploadedImages = (
  uploadedFileParts: PartitionFileObjectsType,
  serviceLocationDetails: ServiceLocationDetailsObjectType,
  updateStoreByKey: (key: string, value: any) => void,
) => {
  const {
    uploadedImages,
    uploadedImagesFileName,
    uploadedImagesFileType,
    uploadedImagesBlob,
    geoDataArrays,
  } = uploadedFileParts;
  const fileDetailsObjects = uploadedImagesFileName.map((filename, index) => ({
    name: filename,
    path: uploadedImagesBlob[index].blob_uri,
  }));
  // update the service location details with the first geo data found
  if (
    !serviceLocationDetails.latitude &&
    !serviceLocationDetails.longitude &&
    geoDataArrays.length > 0
  ) {
    geoDataArrays.some((geoData) => {
      if (
        geoData.latitude !== null &&
        geoData.longitude !== null &&
        geoData.fileName !== null
      ) {
        updateStoreByKey('fileWithLoadedCoordinates', geoData.fileName);
        updateStoreByKey('serviceLocationDetails', {
          ...serviceLocationDetails,
          latitude: geoData.latitude,
          longitude: geoData.longitude,
        });
        // break the loop if the first geo data is found
        return true;
      }
      // continue the loop
      return false;
    });
  }
  // update the service location details with the first geo data found
  updateStoreByKey('uploadedImages', uploadedImages);
  updateStoreByKey('uploadedImagesFileName', uploadedImagesFileName);
  updateStoreByKey('uploadedImagesFileType', uploadedImagesFileType);
  updateStoreByKey('uploadedImagesBlob', uploadedImagesBlob);
  updateStoreByKey('fileDetailsObjects', fileDetailsObjects);
};
